import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { Modal } from "react-bootstrap";

import useEsTelefono from "../../../../utils/esTelefono";
import "../../managment/enrollmentUpdates/styles.css";
import "../../managment/cursos/cursos.css";
import "./tienda.css";

/* Piezas de Tienda (Productos y Bodegas) con la forma de los módulos nuevos (2026-11-03). */

/** Ventana con X arriba (la misma forma de Cursos, Matrículas y Horarios). onHide decide qué cierra Escape. */
export const VentanaTienda: React.FC<{
	titulo: React.ReactNode; onHide: () => void; busy?: boolean; pie?: React.ReactNode; xl?: boolean; className?: string; enforceFocus?: boolean; children: React.ReactNode;
}> = ({ titulo, onHide, busy, pie, xl, className, enforceFocus = true, children }) => {
	const movil = useEsTelefono(760);
	// Escribiendo en un campo, Escape solo sale del campo: no cierra la ventana ni tira lo escrito. La X sí cierra.
	const hide = () => {
		if (busy) return;
		const focused = document.activeElement as HTMLElement | null;
		if (focused && ["INPUT", "TEXTAREA", "SELECT"].includes(focused.tagName) && focused.closest(".modal")) { focused.blur(); return; }
		onHide();
	};
	return <Modal className={"waModulo wcuVentana " + (className || "")} show onHide={hide} enforceFocus={enforceFocus} size={xl ? "xl" : "lg"} centered scrollable fullscreen="sm-down">
		<Modal.Header closeButton={!busy}>
			<Modal.Title style={{ fontSize: movil ? 16 : 19 }}>{titulo}</Modal.Title>
		</Modal.Header>
		<Modal.Body className="wau" style={{ textAlign: "left", fontSize: 15, padding: movil ? "14px" : "18px 22px" }}>{children}</Modal.Body>
		{pie ? <Modal.Footer className="wcuPie" style={{ justifyContent: "flex-end" }}>{pie}</Modal.Footer> : null}
	</Modal>;
};

export interface OpcionMenu { label: string; onClick: () => void; peligro?: boolean; disabled?: boolean }

/**
 * «•••» con las acciones que no se usan a cada rato. Menú propio (no el de react-bootstrap): se pinta junto al botón con
 * position fixed, así no lo corta el scroll horizontal de la tabla ni se va a otra esquina de la pantalla.
 */
export const MenuMas: React.FC<{ opciones: (OpcionMenu | null | false)[]; disabled?: boolean }> = ({ opciones, disabled }) => {
	const visibles = opciones.filter(Boolean) as OpcionMenu[];
	const boton = useRef<HTMLButtonElement>(null);
	const [pos, setPos] = useState<{ top: number; right: number; arriba: boolean } | null>(null);
	useEffect(() => {
		if (!pos) return;
		const cerrar = () => setPos(null);
		const afuera = (event: MouseEvent) => { const t = event.target as Node; if (!boton.current?.contains(t) && !(t as HTMLElement).closest?.(".wtiMenu")) cerrar(); };
		const tecla = (event: KeyboardEvent) => { if (event.key === "Escape") { event.stopPropagation(); cerrar(); } };
		document.addEventListener("mousedown", afuera);
		document.addEventListener("keydown", tecla, true);
		window.addEventListener("scroll", cerrar, true);
		window.addEventListener("resize", cerrar);
		return () => { document.removeEventListener("mousedown", afuera); document.removeEventListener("keydown", tecla, true); window.removeEventListener("scroll", cerrar, true); window.removeEventListener("resize", cerrar); };
	}, [pos]);
	if (visibles.length < 1) return null;
	const abrir = () => {
		if (pos) { setPos(null); return; }
		const r = boton.current!.getBoundingClientRect();
		const alto = visibles.length * 40 + 10;
		const arriba = r.bottom + alto + 8 > window.innerHeight && r.top > alto + 8;
		setPos({ top: arriba ? r.top - alto - 4 : r.bottom + 4, right: Math.max(8, window.innerWidth - r.right), arriba });
	};
	return <>
		<button ref={boton} type="button" className="wtiMenuBoton" aria-label="Más opciones" aria-haspopup="menu" aria-expanded={!!pos} disabled={disabled} onClick={abrir}><IconoPuntos /></button>
		{pos ? ReactDOM.createPortal(<div className="wtiMenu dropdown-menu show" role="menu" style={{ position: "fixed", top: pos.top, right: pos.right, left: "auto", zIndex: 2200 }}>
			{visibles.map((opcion) => <button key={opcion.label} type="button" role="menuitem" className={"dropdown-item" + (opcion.peligro ? " peligro" : "")} disabled={opcion.disabled}
				onClick={() => { setPos(null); opcion.onClick(); }}>{opcion.label}</button>)}
		</div>, document.body) : null}
	</>;
};

export const IconoPuntos: React.FC = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="5" cy="12" r="2.2" /><circle cx="12" cy="12" r="2.2" /><circle cx="19" cy="12" r="2.2" /></svg>;
export const IconoLapiz: React.FC = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" /></svg>;
export const IconoLupa: React.FC = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>;
export const IconoAmpliar: React.FC = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true"><path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" /></svg>;

/** Buscador con lupa (forma .wau-buscar). */
export const Buscador: React.FC<{ value: string; placeholder: string; disabled?: boolean; onChange: (value: string) => void }> = ({ value, placeholder, disabled, onChange }) =>
	<div className="wau-buscar">
		<IconoLupa />
		<input value={value} placeholder={placeholder} aria-label={placeholder} disabled={disabled} onChange={(event) => onChange(event.target.value)} />
	</div>;

/** Elegir una de pocas opciones (forma .wau-seg). */
export function Segmento<T extends string>({ opciones, value, disabled, onChange }: { opciones: [T, string][]; value: T; disabled?: boolean; onChange: (value: T) => void }) {
	return <div className="wau-seg" role="group">
		{opciones.map(([key, label]) => <button key={key} type="button" aria-pressed={value === key} disabled={disabled} onClick={() => onChange(key)}>{label}</button>)}
	</div>;
}

export interface Seleccionado { id: number; name: string; waCode: string | null; kind?: string; onHand?: number; variantCount?: number; modelId?: number | null }

/** Barra oscura abajo cuando hay productos marcados con el cuadrito. */
export const BarraSeleccion: React.FC<{ items: Seleccionado[]; onQuitar: (id: number) => void; onLimpiar: () => void; children: React.ReactNode }> = ({ items, onQuitar, onLimpiar, children }) => {
	if (items.length < 1) return null;
	return <div className="wau-lote wti-lote" role="region" aria-label="Seleccionados">
		<div className="wti-lote-nombres">
			<b>{items.length} {items.length === 1 ? "seleccionado" : "seleccionados"}</b>
			{items.slice(0, 6).map((item) => <span key={item.id} className="wti-lote-chip" title={item.name}><span>{item.name}</span>
				<button type="button" aria-label={"Quitar " + item.name} onClick={() => onQuitar(item.id)}>×</button></span>)}
			{items.length > 6 ? <span style={{ fontSize: 13, opacity: .8 }}>y {items.length - 6} más</span> : null}
		</div>
		<div style={{ display: "flex", gap: 8, alignItems: "center" }}>
			<button type="button" className="wau-btn s mini" onClick={onLimpiar}>Quitar Selección</button>
			{children}
		</div>
	</div>;
};
